import { getJson, getList, sendJson, type PaginationMeta } from '@/lib/api';

/**
 * The Salesforce Lead mirror — typed fetchers over /api/v3/leads. Shapes
 * mirror apps/api's LeadRecord / LeadStats / LeadSyncStatus.
 */

export const STATS_INTERVAL = { day: 'day', week: 'week', month: 'month' } as const;
export type StatsInterval = (typeof STATS_INTERVAL)[keyof typeof STATS_INTERVAL];

export interface LeadStatsPoint {
  readonly period: string;
  readonly leads: number;
  /** Leads in the bucket that went on to a signed contract. */
  readonly signed: number;
}

export interface LeadBreakdownRow {
  readonly key: string;
  readonly leads: number;
  readonly signed: number;
  /** Total_Cost__c of the signed contracts. */
  readonly value: number;
}

/** What became of a lead — its converted opportunity's stage. */
export const LEAD_OUTCOME = {
  signed: 'signed',
  cancelled: 'cancelled',
  open: 'open',
  lost: 'lost',
  unqualified: 'unqualified',
  none: 'none',
} as const;
export type LeadOutcome = (typeof LEAD_OUTCOME)[keyof typeof LEAD_OUTCOME];

/** Whether a lead counts as a lead (mirrors apps/api LEAD_CLASSIFICATION). */
export const LEAD_CLASSIFICATION = {
  counted: 'counted',
  junk: 'junk',
  duplicate: 'duplicate',
  neverWorked: 'never_worked',
  test: 'test',
} as const;
export type LeadClassification = (typeof LEAD_CLASSIFICATION)[keyof typeof LEAD_CLASSIFICATION];

/** Why a lead got its classification (mirrors apps/api CLASSIFICATION_REASON). */
export const CLASSIFICATION_REASON = {
  testCompany: 'test_company',
  internalEmail: 'internal_email',
  closedDuplicate: 'closed_duplicate',
  repeatInquiry: 'repeat_inquiry',
  leadStatus: 'lead_status',
  closedUnqualified: 'closed_unqualified',
  notConverted: 'not_converted',
  realLead: 'real_lead',
} as const;
export type ClassificationReason = (typeof CLASSIFICATION_REASON)[keyof typeof CLASSIFICATION_REASON];

/** What the classification job stored on a lead. */
export interface LeadAnalytics {
  readonly classification: LeadClassification;
  readonly reason: ClassificationReason;
  readonly outcome: LeadOutcome;
  /** Whether its opportunity ever reached a proposal stage. */
  readonly proposalSent: boolean;
}

/** A person's include / exclude call on one lead. */
export interface LeadAnalyticsOverride {
  readonly include: boolean;
  readonly by: string;
  readonly at: string;
}

export const STATS_SEGMENT = { channel: 'channel', form: 'form', owner: 'owner' } as const;
export type StatsSegment = (typeof STATS_SEGMENT)[keyof typeof STATS_SEGMENT];

export interface LeadSegmentSeries {
  readonly key: string;
  readonly values: readonly number[];
  readonly total: number;
}

export interface LeadStats {
  readonly range: {
    readonly from: string;
    readonly to: string;
    readonly interval: StatsInterval;
    /** Buckets at the start of `series` that precede `from` (for trailing averages). */
    readonly leadIn: number;
  };
  readonly totals: {
    readonly leads: number;
    readonly signed: number;
    readonly signedValue: number;
    readonly cancelled: number;
    readonly open: number;
    readonly lost: number;
    readonly unqualified: number;
    readonly none: number;
    readonly paid: number;
    readonly previousLeads: number;
    readonly changePct: number | null;
    readonly perDay: number;
  };
  readonly series: readonly LeadStatsPoint[];
  readonly segments: readonly LeadSegmentSeries[] | null;
  readonly yoy: readonly number[] | null;
  readonly byChannel: readonly LeadBreakdownRow[];
  readonly bySource: readonly LeadBreakdownRow[];
  readonly byForm: readonly LeadBreakdownRow[];
  readonly byOwner: readonly LeadBreakdownRow[];
  /**
   * Opportunities created in the window that no Lead converted into, beside
   * the lead count and never in it. Absent from an API older than this page,
   * which then shows nothing for it (the two deploy minutes apart).
   */
  readonly withoutLead?: WithoutLeadStats;
}

/** Opportunities reps created straight in Salesforce (mirrors the API's lead-direct-opportunities.ts). */
export interface WithoutLeadStats {
  readonly total: number;
  readonly previous: number;
  readonly changePct: number | null;
  /** Every opportunity created in the window, with a lead or without. */
  readonly opportunities: number;
  readonly signed: number;
  readonly signedValue: number;
  /** Per bucket, aligned with `series` (lead-in buckets included). */
  readonly series: readonly number[];
  /**
   * Of `series`, how many per bucket signed. Absent from an API older than
   * the Signed chart view, which then shows the leads' bars alone.
   */
  readonly signedSeries?: readonly number[];
  /** By the opportunity's source channel; `leads` holds the opportunity count. */
  readonly bySource: readonly LeadBreakdownRow[];
}

/** Sortable columns on GET /leads (mirrors apps/api LEAD_SORT_FIELDS). */
export const LEAD_SORT_KEYS = [
  'createdAt',
  'name',
  'company',
  'owner',
  'channel',
  'eventDate',
  'status',
] as const;
export type LeadSortKey = (typeof LEAD_SORT_KEYS)[number];

export interface Lead {
  readonly id: string;
  readonly salesforceId: string;
  readonly name: string;
  readonly email: string;
  readonly company: string;
  readonly title: string;
  readonly phone: string;
  readonly city: string;
  readonly state: string;
  readonly status: string;
  readonly leadSource: string;
  readonly formCompleted: string;
  readonly ownerName: string;
  readonly salesperson: string;
  readonly createdByName: string;
  readonly channel: string;
  readonly isConverted: boolean;
  readonly convertedAt: string | null;
  readonly convertedOpportunityId: string | null;
  readonly outcome: LeadOutcome;
  readonly booking: { stageName: string; signedAt: string | null; totalCost: number | null } | null;
  readonly eventCity: string;
  readonly eventDate: string | null;
  readonly participantCount: number | null;
  readonly programSelections: readonly string[];
  /** The prospect's own message ("Describe your group…"), else the Lead's notes. */
  readonly description: string;
  readonly country: string;
  readonly howDidYouHear: string;
  readonly attribution: {
    readonly gclid: string;
    readonly utmSource: string;
    readonly utmMedium: string;
    readonly utmCampaign: string;
    readonly utmTerm: string;
    readonly landingPage: string;
    readonly referringPage: string;
    /** The page the form was submitted on. */
    readonly convertPage: string;
  };
  readonly sfCreatedAt: string;
  /** Null until the classification job has seen the lead. */
  readonly analytics: LeadAnalytics | null;
  /** Null while the classification decides. */
  readonly analyticsOverride: LeadAnalyticsOverride | null;
  /** Whether it counts as a lead (override, else classification); null until classified. */
  readonly counted: boolean | null;
  /** False when it is left out of every lead analytic. */
  readonly inAnalytics: boolean;
}

/** What PATCH /leads/:id/analytics-override returns. */
export type LeadAnalyticsState = Pick<Lead, 'id' | 'analytics' | 'analyticsOverride' | 'counted' | 'inAnalytics'>;

/** One pass of the leads ingest job (apps/api IngestRunRecord). */
export interface LeadSyncRun {
  readonly id: string;
  readonly job: string;
  readonly startedAt: string;
  readonly finishedAt: string | null;
  readonly mode: 'backfill' | 'incremental';
  readonly fetched: number;
  readonly upserted: number;
  readonly error: string | null;
}

export interface LeadSyncStatus {
  readonly leads: number;
  readonly latestSystemModstamp: string | null;
  readonly lastRun: LeadSyncRun | null;
  readonly runs: readonly LeadSyncRun[];
}

export interface LeadSyncSummary {
  readonly mode: 'backfill' | 'incremental';
  readonly fetched: number;
  readonly upserted: number;
  readonly pages: number;
  readonly ms: number;
}

/** Where a lead opens in Salesforce. */
export function salesforceLeadUrl(salesforceId: string): string {
  return `https://bestcorporateevents.lightning.force.com/${salesforceId}`;
}

/** Where one lead's page lives in the app. */
export function leadPath(id: string): string {
  return `/leads/${id}`;
}

// ─── Time of day (GET /leads/timing) — mirrors apps/api's LeadTiming ───────

export interface HourPercentiles {
  readonly mean: number;
  readonly p25: number;
  readonly p50: number;
  readonly p75: number;
  readonly p90: number;
}

export interface LeadTimingHour {
  readonly hour: number;
  readonly leads: number;
  readonly share: number;
  readonly cumulativeShare: number;
  readonly previousShare: number | null;
}

export interface LeadTimingMonth {
  readonly month: string;
  readonly leads: number;
  readonly workingDays: number;
  readonly perWorkingDay: number;
  readonly peakHour: number | null;
  readonly shareBeforeNoon: number;
  readonly shareAfterFive: number;
}

export interface LeadTimingToday {
  readonly day: string;
  readonly isWorkingDay: boolean;
  readonly asOfHour: number;
  readonly byHour: readonly number[];
  readonly soFar: number;
  readonly expectedSoFar: { readonly p50: number; readonly p90: number; readonly mean: number };
  readonly expectedTotal: { readonly p50: number; readonly p90: number };
}

export interface LeadTiming {
  readonly range: { readonly from: string; readonly to: string };
  readonly leads: number;
  readonly excluded: number;
  readonly workingDays: number;
  readonly hours: readonly LeadTimingHour[];
  readonly weekdayHour: readonly (readonly number[])[];
  readonly weekdayTotals: readonly number[];
  readonly typicalWorkingDay: {
    readonly total: HourPercentiles;
    readonly hours: readonly (HourPercentiles & { readonly hour: number })[];
    readonly cumulative: readonly (HourPercentiles & { readonly hour: number })[];
  };
  readonly months: readonly LeadTimingMonth[];
  readonly today: LeadTimingToday;
}

// ─── Month pace (GET /leads/pacing) — mirrors apps/api's LeadPacing ────────

export const PACING_COMPARISON = { lastMonth: 'last_month', lastYear: 'last_year' } as const;
export type PacingComparisonKey = (typeof PACING_COMPARISON)[keyof typeof PACING_COMPARISON];

export interface LeadPacingComparison {
  readonly key: PacingComparisonKey;
  /** YYYY-MM. */
  readonly month: string;
  readonly businessDays: number;
  /** Leads through the same number of completed business days as the current month. */
  readonly atSamePoint: number;
  readonly total: number;
  /** Current month against `atSamePoint`, in percent; null when that month had none by then. */
  readonly changePct: number | null;
}

export interface LeadPacingDay {
  /** Business day of the month, from 1. */
  readonly index: number;
  /** That business day's date in the current month; null past its last one. */
  readonly day: string | null;
  /** Running total this month; null for days that have not started. */
  readonly current: number | null;
  readonly lastMonth: number | null;
  readonly lastYear: number | null;
}

export interface LeadPacing {
  /** YYYY-MM. */
  readonly month: string;
  readonly today: string;
  readonly businessDays: number;
  /** Business days already over; today never counts. */
  readonly completedBusinessDays: number;
  /** Counted leads so far this month, today included. */
  readonly monthToDate: number;
  /** Counted leads through the completed business days. */
  readonly atCompleted: number;
  readonly perBusinessDay: number | null;
  /** The month's total if the daily rate holds. */
  readonly projected: number | null;
  readonly comparisons: readonly LeadPacingComparison[];
  readonly days: readonly LeadPacingDay[];
}

/** The current month's counted leads by business day, against last month and last year. */
export function readLeadPacing(signal?: AbortSignal): Promise<LeadPacing> {
  return getJson<LeadPacing>('/leads/pacing', undefined, signal);
}

/** Defaults to the trailing 52 weeks; pass from/to (YYYY-MM-DD, to exclusive) to override. */
export function readLeadTiming(params: { from?: string; to?: string } = {}, signal?: AbortSignal): Promise<LeadTiming> {
  return getJson<LeadTiming>('/leads/timing', params, signal);
}

export function readLeadStats(
  params: {
    from: string;
    to: string;
    interval: StatsInterval;
    leadIn?: number;
    segment?: StatsSegment;
    yoy?: '1';
    /** Count every lead, including junk and never-worked ones (left out by default). */
    unqualified?: '1';
  },
  signal?: AbortSignal,
): Promise<LeadStats> {
  return getJson<LeadStats>('/leads/stats', params, signal);
}

export function listLeads(
  params: {
    search?: string;
    from?: string;
    to?: string;
    page?: number;
    limit?: number;
    sort?: string;
    dir?: 'asc' | 'desc';
    /** Only leads the classification job filed this way. */
    classification?: LeadClassification;
    /** Only leads that count as a lead (`1`) or do not (`0`). */
    counted?: '1' | '0';
  },
  signal?: AbortSignal,
): Promise<{ items: Lead[]; pagination: PaginationMeta }> {
  return getList<Lead>('/leads', params, signal);
}

/** One lead, with the prospect's message. */
export function readLead(id: string, signal?: AbortSignal): Promise<Lead> {
  return getJson<Lead>(`/leads/${id}`, undefined, signal);
}

/**
 * Includes or excludes one lead from analytics, or hands the call back to
 * the classification (`include: null`).
 */
export function setLeadAnalyticsOverride(id: string, include: boolean | null): Promise<LeadAnalyticsState> {
  return sendJson<LeadAnalyticsState>('patch', `/leads/${id}/analytics-override`, { include });
}

export function readLeadSyncStatus(signal?: AbortSignal): Promise<LeadSyncStatus> {
  return getJson<LeadSyncStatus>('/leads/sync/status', undefined, signal);
}

export function syncLeadsNow(): Promise<LeadSyncSummary> {
  return sendJson<LeadSyncSummary>('post', '/leads/sync');
}
